import {authFetch} from "../../authentication/authFetch";

const API_URL = process.env.REACT_APP_API_URL;

export function normalizeRecallCard(card) {
    if (!card) return null;

    const cardWithoutEaseFactor = { ...card };
    delete cardWithoutEaseFactor.easeFactor;
    delete cardWithoutEaseFactor.ease_factor;

    const userId = card.userId ?? card.user_id ?? null;
    const createdDate = card.createdDate ?? card.created_date ?? null;
    const endDate = card.endDate ?? card.end_date ?? null;
    const parentFolderId = card.parentFolderId ?? card.parent_folder_id ?? null;
    const lastRevisionDate = card.lastRevisionDate ?? card.last_revision_date ?? null;
    const nextRevisionDate = card.nextRevisionDate ?? card.next_revision_date ?? null;
    const numberOfRevisions = card.numberOfRevisions ?? card.number_of_revisions ?? 0;
    const intervalDays = card.intervalDays ?? card.interval_days ?? 0;
    const difficulty = card.difficulty ?? null;
    const stability = card.stability ?? null;
    const stepIndex = card.stepIndex ?? card.step_index ?? null;
    const learningState = card.learningState ?? card.learning_state ?? null;
    const avgRecallTime = card.avgRecallTime ?? card.avg_recall_time ?? 0;
    const successRate = card.successRate ?? card.success_rate ?? 0;
    const correctCount = card.correctCount ?? card.correct_count ?? 0;
    const wrongCount = card.wrongCount ?? card.wrong_count ?? 0;
    const sourceNoteId = card.sourceNoteId ?? card.source_note_id ?? null;
    const aiGenerated = card.aiGenerated ?? card.ai_generated ?? false;

    return {
        ...cardWithoutEaseFactor,
        userId,
        user_id: userId,
        createdDate,
        created_date: createdDate,
        endDate,
        end_date: endDate,
        parentFolderId,
        parent_folder_id: parentFolderId,
        lastRevisionDate,
        last_revision_date: lastRevisionDate,
        nextRevisionDate,
        next_revision_date: nextRevisionDate,
        numberOfRevisions,
        number_of_revisions: numberOfRevisions,
        intervalDays,
        interval_days: intervalDays,
        difficulty,
        stability,
        stepIndex,
        step_index: stepIndex,
        learningState,
        learning_state: learningState,
        avgRecallTime,
        avg_recall_time: avgRecallTime,
        successRate,
        success_rate: successRate,
        correctCount,
        correct_count: correctCount,
        wrongCount,
        wrong_count: wrongCount,
        sourceNoteId,
        source_note_id: sourceNoteId,
        aiGenerated,
        ai_generated: aiGenerated,
    };
}

export function serializeRecallCard(cardData) {
    return {
        user_id: cardData.userId ?? cardData.user_id ?? null,
        tags: cardData.tags ?? '',
        created_date: cardData.createdDate ?? cardData.created_date ?? null,
        end_date: cardData.endDate ?? cardData.end_date ?? null,
        front: cardData.front ?? '',
        back: cardData.back ?? '',
        parent_folder_id: cardData.parentFolderId ?? cardData.parent_folder_id ?? null,
        source_note_id: cardData.sourceNoteId ?? cardData.source_note_id ?? null,
        ai_generated: cardData.aiGenerated ?? cardData.ai_generated ?? false,
        hint: cardData.hint ?? '',
    };
}

export const fetchDueRecallCardCount = async (authToken) => {
    if (!authToken) return 0;
    const response = await authFetch(`${API_URL}/api/recallcards/due/count`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) throw new Error('Failed to fetch due recall card count');
    return response.json();
};

export const fetchRecallStats = async (authToken, folderId = null) => {
    if (!authToken) return null;
    const url = new URL(`${API_URL}/api/recallcards/stats`);
    if (folderId) url.searchParams.set('folderId', folderId);
    const response = await authFetch(url.toString(), {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) throw new Error('Failed to fetch recall stats');
    return response.json();
};

export const fetchDueRecallCards = async (authToken) => {
    if (!authToken) return [];
    const response = await authFetch(`${API_URL}/api/recallcards/due`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) throw new Error('Failed to fetch due recall cards');
    const payload = await response.json();
    const cards = Array.isArray(payload) ? payload : payload?.items ?? [];

    return cards.map(normalizeRecallCard).filter(Boolean);
};

export const fetchRecallCards = async (authToken, folderId = null, sourceNoteId = null) => {
    if (!authToken) return [];
    const url = new URL(`${API_URL}/api/recallcards`);
    if (folderId) url.searchParams.set('folderId', folderId);
    if (sourceNoteId) url.searchParams.set('sourceNoteId', sourceNoteId);
    const response = await authFetch(url.toString(), {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) throw new Error('Failed to fetch recall cards');
    const payload = await response.json();
    const cards = Array.isArray(payload) ? payload : payload?.items ?? [];

    return cards.map(normalizeRecallCard).filter(Boolean);
};

export const fetchRecallCardsForNote = async (authToken, noteId) => {
    if (!authToken || !noteId) return [];
    const response = await authFetch(`${API_URL}/api/recallcards?sourceNoteId=${noteId}`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) throw new Error('Failed to fetch recall cards for note');
    const payload = await response.json();
    const cards = Array.isArray(payload) ? payload : payload?.items ?? [];

    return cards.map(normalizeRecallCard).filter(Boolean);
};

export const fetchRecallCard = async (authToken, id) => {
    if (!authToken) return null;
    const response = await authFetch(`${API_URL}/api/recall/${id}`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) throw new Error('Failed to fetch recall card');
    return normalizeRecallCard(await response.json());
};


export const saveRecallCard = async (authToken, cardData) => {
    if (!authToken) throw new Error('Missing auth token');
    const response = await authFetch(`${API_URL}/api/recallcard`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(serializeRecallCard(cardData)),
    });
    if (!response.ok) throw new Error('Failed to save recall card');

    return normalizeRecallCard(await response.json());
};

export const updateRecallCard = async (authToken, recallCardId, cardData) => {
    if (!authToken) throw new Error('Missing auth token');
    const response = await authFetch(`${API_URL}/api/recall/${recallCardId}`, {
        method: 'PUT',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(serializeRecallCard(cardData)),
    });
    if (!response.ok) throw new Error('Failed to update recall card');

    return normalizeRecallCard(await response.json());
};

export const deleteRecallCard = async (authToken, recallCardId) => {
    if (!authToken) throw new Error('Missing auth token');
    const response = await authFetch(`${API_URL}/api/recall/${recallCardId}`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${authToken}`,
        },
    });
    if (!response.ok) throw new Error('Failed to delete recall card');

    return response.json();
};

export const reviewRecallCard = async (authToken, recallCardId, reviewData) => {
    if (!authToken) throw new Error('Missing auth token');
    const response = await authFetch(`${API_URL}/api/recall/${recallCardId}/review`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            rating: reviewData.rating,
            recall_time_ms: reviewData.recall_time_ms,
        }),
    });
    if (!response.ok) throw new Error('Failed to review recall card');

    return normalizeRecallCard(await response.json());
};
